import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Row, Col, Card, CardHeader } from "reactstrap";
import { reportsApi } from "../../../../services/reportsServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import { utils } from "../../../../utils/utils";

const INCLUDED_LABOR_CODES = ["14", "18"];
ChartJS.register(ArcElement, Tooltip, Legend);

const generateColors = (count) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const hue = (i * 360) / count;
    const saturation = 75;
    const lightness = 50;
    colors.push(`hsl(${hue}, ${saturation}%, ${lightness}%)`);
  }
  return colors;
};

const ManHoursChart = () => {
  const { id: jobId } = useParams();
  const [rawData, setRawData] = useState(null);
  const [managementRoleData, setManagementRoleData] = useState(null);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const result = await reportsApi.manHoursByLaborCode({ jobId });
        if (result && result.data) {
          setRawData(result.data);
        } else {
          setRawData(null);
        }
      } catch (error) {
        setRawData(null);
        console.error("Error fetching chart data:", error);
      }
    };

    const fetchManagementRoleData = async () => {
      try {
        const result = await reportsApi.manHoursByManagementRole({ jobId });
        setManagementRoleData(result.data);
      } catch (error) {
        console.error("Error fetching management role data:", error);
      }
    };

    fetchChartData();
    fetchManagementRoleData();
  }, [jobId]);

  const { chartData, totalShopHours, totalFabricationHours, fabricationCodes } =
    useMemo(() => {
      if (!rawData || !rawData.laborCategories) {
        return {
          chartData: {
            labels: [],
            datasets: [{ data: [], backgroundColor: [] }],
          },
          totalShopHours: 0,
          totalFabricationHours: 0,
          fabricationCodes: [],
        };
      }

      const { laborCategories } = rawData;

      const {
        fabricationLaborCategories,
        filteredCategories,
        totalFabricationHours,
      } = laborCategories.reduce(
        (acc, item) => {
          const hours = parseFloat(item.totalEstimatedHours);
          if (INCLUDED_LABOR_CODES.includes(item.code)) {
            acc.fabricationLaborCategories.push(item);
            acc.totalFabricationHours += hours;
          }
          acc.filteredCategories.push(item);
          return acc;
        },
        {
          fabricationLaborCategories: [],
          filteredCategories: [],
          totalFabricationHours: 0,
        }
      );

      const chartLabels = filteredCategories.map((item) => `LC-${item.code}`);
      const chartValues = filteredCategories.map((item) =>
        parseFloat(item.totalEstimatedHours)
      );
      const chartColors = generateColors(chartLabels.length);

      return {
        chartData: {
          labels: chartLabels,
          datasets: [
            {
              data: chartValues,
              backgroundColor: chartColors,
              borderWidth: 0,
            },
          ],
          legendData: filteredCategories.map((item, index) => ({
            label: `LC-${item.code}`,
            value: parseFloat(item.totalEstimatedHours),
            color: chartColors[index],
          })),
        },
        totalShopHours: utils.roundBasedOnThirdDecimalDigit(
          rawData.totalEstimatedHours || 0
        ),
        totalFabricationHours,
        fabricationCodes: fabricationLaborCategories,
      };
    }, [rawData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || "";
            const value = context.parsed || 0;
            return `${label} ${value}`;
          },
        },
      },
    },
    cutout: "80%",
  };

  const legendColumns = useMemo(() => {
    if (!chartData.legendData) return [[], []];
    const midIndex = Math.ceil(chartData.legendData.length / 2);
    return [
      chartData.legendData.slice(0, midIndex),
      chartData.legendData.slice(midIndex),
    ];
  }, [chartData.legendData]);

  return (
    <Card className="p-3 m-0 h-100">
      <CardHeader className="bg-white border-0 p-0 mb-3">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center p-2 bg-soft-blue">
              <FontAwesomeIcon icon={faClock} size="lg" />
            </div>
            <h4 className="m-0 ml-3 font-weight-bold">Man hours</h4>
          </div>
        </div>
      </CardHeader>
      <Row>
        <Col>
          <Row className="mb-3">
            <Col xs="6">
              <div className="h2 font-weight-bold">{totalShopHours}</div>
              <div>Total Shop Man Hours</div>
            </Col>
            <Col xs="6">
              <div className="h2 font-weight-bold">
                {managementRoleData?.totalEstimatedHours || 0}
              </div>
              <div>Total Engineering Hours</div>
            </Col>
          </Row>
          <Row>
            {legendColumns.map((column, colIndex) => (
              <Col xs="6" key={colIndex}>
                {column.map((item, index) => (
                  <div key={index} className="d-flex justify-content-between">
                    <div>
                      <span
                        style={{
                          display: "inline-block",
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: item.color,
                          marginRight: "8px",
                        }}
                      />
                      <span className="font-weight-bold">{item.label}</span>
                    </div>
                    <span>{item.value}</span>
                  </div>
                ))}
              </Col>
            ))}
          </Row>
        </Col>
        <Col xs="5" className="d-flex flex-column justify-content-center">
          <div style={{ height: "90%" }}>
            <Doughnut data={chartData} options={chartOptions} />
          </div>
        </Col>
      </Row>
      <hr className="my-3" />
      <Row className="align-items-center">
        <Col xs="9">
          <div>Total Fabrication Hours</div>
          <div className="h2 font-weight-bold m-0">{totalFabricationHours}</div>
        </Col>
        <Col xs="3">
          {fabricationCodes.map((item) => (
            <div key={item.code} className="d-flex justify-content-between">
              <span className="font-weight-bold">LC-{item.code}</span>
              <span>{parseFloat(item.totalEstimatedHours)}</span>
            </div>
          ))}
        </Col>
      </Row>
    </Card>
  );
};

export default ManHoursChart;
